import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal, input } from '@angular/core';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {
  getPaginatedWidgetAvatarTemplates,
  getTemplateError,
  getTemplateSuccess,
  ITemplateState,
  ResetTemplateState,
  WidgetAvatarTemplateCreate,
  WidgetAvatarTemplateDelete,
  WidgetAvatarTemplateGenerate,
  getWidgetAvatars,
  WidgetAvatarTemplateList,
  WidgetAvatarTemplateUpdate
} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {WidgetAvatarTemplate} from '@app/modules/shared/models';
import { NgbActiveModal, NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import Swal from 'sweetalert2';

import {SbTableComponent} from '@app/_components/unified/sb-table/sb-table.component';
import {SbTableActionsDirective, SbTableCellDirective} from '@app/_components/unified/sb-table/sb-table.directives';
import {SbTableColumn, SbTableOptions, SbTableServerState} from '@app/_components/unified/sb-table/sb-table.types';
@Component({
  selector: 'app-widget-avatar-templates',
  templateUrl: './widget-avatar-templates.component.html',
  styleUrls: ['./widget-avatar-templates.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbTableComponent, SbTableCellDirective, SbTableActionsDirective, NgbPopover, ReactiveFormsModule]
})
export class WidgetAvatarTemplatesComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private toastr = inject(ToastrService);
  modal = inject(NgbActiveModal);
  private _modalService = inject(NgbModal);
  private templateStore = inject<Store<ITemplateState>>(Store);
  private formBuilder = inject(FormBuilder);

  readonly tableColumns: SbTableColumn[] = [
    {id: 'name', header: 'Name', headerClass: 'w-25', className: 'w-25 text-wrap'},
    {id: 'image', header: 'Image'},
  ];
  // The dialog kept its page, search and length in the address before; it still does.
  readonly tableOptions: SbTableOptions = {
    serverSide: true,
    urlState: true,
    selectable: false,
    ordering: false,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
    tableId: 'dt-table',
  };
  readonly totalRows = signal(0);
  unsubscriber = new Subject();
  readonly data = input<any>(undefined);
  /** The page of templates the table shows. */
  readonly widgetAvatarTemplates = signal<WidgetAvatarTemplate[]>([]);
  selectedAction = '';
  addWidgetAvatarTemplateForm: FormGroup<{
    name: FormControl<string | null>;
    prompt: FormControl<string | null>;
    prompt_revised: FormControl<string | null>;
    image_variations: FormControl<any>;
  }>;
  submitted = false;
  // staticImageVariations: any = [
  //   {name: 'original', url: 'https://fastly.picsum.photos/id/16/2500/1667.jpg?hmac=uAkZwYc5phCRNFTrV_prJ_0rP0EdwJaZ4ctje2bY7aE'},
  //   {name: 'silvertone', url: 'https://fastly.picsum.photos/id/15/2500/1667.jpg?hmac=Lv03D1Y3AsZ9L2tMMC1KQZekBVaQSDc1waqJ54IHvo4'},
  //   {name: 'light', url: 'https://fastly.picsum.photos/id/16/2500/1667.jpg?hmac=uAkZwYc5phCRNFTrV_prJ_0rP0EdwJaZ4ctje2bY7aE'}
  // ];
  // The template dialog is a view of its own, outside this one: the images it shows, generated
  // after it opens, are in signals.
  readonly selectedVariation = signal<any>(null);
  widgetAvatarTemplateId = null;
  customizeTemplateModal = null;
  readonly imageVariations = signal<any[]>([]);
  deleteAction = false;

  constructor() {
    this.templateStore.dispatch(ResetTemplateState({params: {error: '', paginatedWidgetAvatarTemplates: null, widgetAvatarTemplate: null}}));
  }

  ngOnInit() {
    this.subscribeStore();
    this.addWidgetAvatarTemplateForm = this.formBuilder.group({
      name: ['', Validators.required],
      prompt: ['', Validators.required],
      prompt_revised: [''],
      image_variations: [null, Validators.required]
    });
  }

  /** The table asks for a page: the request the old table's ajax made, unchanged. */
  onTableState(state: SbTableServerState): void {
    this.loadAllWidgetAvatarTemplates(state.page - 1, state.pageLength, state.search);
  }

  subscribeStore() {
    this.templateStore.pipe(select(getPaginatedWidgetAvatarTemplates))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(paginatedTemplates => {
        if (paginatedTemplates) {
          this.widgetAvatarTemplates.set(paginatedTemplates.list);
          this.totalRows.set(paginatedTemplates.paging.total);
        }
      });

    this.templateStore.pipe(select(getTemplateError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.templateStore.pipe(select(getTemplateSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.selectedAction && this.submitted) {
            this.customizeTemplateModal.close();
            this.submitted = false;
            this.addWidgetAvatarTemplateForm.reset();
            this.loadAllWidgetAvatarTemplates();
          }
          if (this.deleteAction) {
            this.deleteAction = false;
            this.loadAllWidgetAvatarTemplates();
          }
        }
      });

    this.templateStore.pipe(select(getWidgetAvatars))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(avatars => {
        if (avatars) {
          this.imageVariations.set([...avatars]);
          if (this.imageVariations().length > 0) {
            this.selectVariation(this.imageVariations()[0], 0);
          }
        }
      });
  }

  loadAllWidgetAvatarTemplates(page = 0, perPage = 10, search = '', orderBy= {column: 'created_at', dir: 'desc'}) {
    this.templateStore.dispatch(WidgetAvatarTemplateList({
      params: {
        project_id: this.data().project_id,
        options: JSON.stringify({includePagination: true, orderBy}),
        page: page + 1,
        perPage,
        search
      }
    }));
  }

  get f() {
    return this.addWidgetAvatarTemplateForm.controls;
  }

  showAddEditTemplateModel(customizeTemplate, type, template = null) {
    this.submitted = false;
    this.selectedAction = type;
    if (type === 'edit') {
      this.handleEditAction(template);
    } else {
      this.handleAddAction();
    }

    const modelRef = this._modalService.open(customizeTemplate, {
      centered: false,
      size: 'md',
      keyboard: false
    });
    modelRef.result.then((result) => {
    }, (reason) => {
    });
  }

  handleEditAction(template) {
    this.widgetAvatarTemplateId = template.id;
    this.imageVariations.set(template?.image_variations || []);
    this.selectedVariation.set(this.imageVariations().find(item => item.isActive) || null);
    if (this.selectedVariation() && !template.prompt_revised) {
      this.addWidgetAvatarTemplateForm.patchValue({
        ...template,
        prompt_revised: this.selectedVariation().revised_prompt
      });
    } else {
      this.addWidgetAvatarTemplateForm.patchValue(template);
    }
  }

  handleAddAction() {
    this.widgetAvatarTemplateId = null;
    this.imageVariations.set([]);
    this.selectedVariation.set(null);
    this.addWidgetAvatarTemplateForm.reset();
    // this.imageVariations = [...this.staticImageVariations];
    // if (this.imageVariations.length > 0) {
    //   if (!this.imageVariations[0].hasOwnProperty('isActive')) {
    //     this.imageVariations[0].isActive = true;
    //   }
    //   this.selectedVariation = this.imageVariations[0];
    // }
    // this.addWidgetAvatarTemplateForm.patchValue({ image_variations: this.imageVariations });
  }

  selectVariation(variation: string, index) {
    this.imageVariations.update((images) => images.map((image, i) => ({
      ...image,
      isActive: i === index
    })));
    this.selectedVariation.set(this.imageVariations().find(item => item.isActive));
    const filteredImages = this.imageVariations().map(({isActive, ...rest}) => isActive ? {isActive, ...rest} : {...rest});
    this.addWidgetAvatarTemplateForm.patchValue({prompt_revised: this.selectedVariation().revised_prompt,image_variations: filteredImages});
  }

  saveWidgetTemplateForm(modal) {
    this.submitted = true;
    if (this.addWidgetAvatarTemplateForm.invalid) {
      return;
    }
    this.customizeTemplateModal = modal;
    if (this.widgetAvatarTemplateId) {
      this.templateStore.dispatch(WidgetAvatarTemplateUpdate({
        id: this.widgetAvatarTemplateId,
        body: {...this.addWidgetAvatarTemplateForm.value, project_id: this.data().project_id}
      }));
    } else {
      this.templateStore.dispatch(WidgetAvatarTemplateCreate({
        body: {...this.addWidgetAvatarTemplateForm.value, project_id: this.data().project_id}
      }));
    }
  }

  deleteWidgetTemplate(templateId: number) {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.deleteAction = true;
        this.templateStore.dispatch(WidgetAvatarTemplateDelete({widgetAvatarTemplateId: templateId}));
      }
    });
  }

  generateWidgetAvatarImages() {
    this.templateStore.dispatch(WidgetAvatarTemplateGenerate({
      body: {
        prompt: this.addWidgetAvatarTemplateForm.value.prompt,
        baseImageUrl: `${window.location.origin}/assets/images/avatars/ai-avatar-base.png`,
      }
    }));
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

}
